import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { api, fmtBRL, fmtInt } from "@/lib/api";
import { apexBase, buildParams, useChartTheme, PALETTE } from "./pcShared";
import { MagnifyingGlass } from "@phosphor-icons/react";

const SKU_GRAN_OPTIONS = [["day", "Dia"], ["week", "Semana"], ["month", "Mês"]];

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-200 dark:border-zinc-700 rounded px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="font-mono text-sm font-bold">{value}</div>
    </div>
  );
}

function SkuDetail({ sku, applied }) {
  const dark = useChartTheme();
  const [data, setData] = useState(null);
  const [gran, setGran] = useState("day");

  useEffect(() => {
    setData(null);
    api.get(`/production-reports/sku/${encodeURIComponent(sku)}`, { params: buildParams(applied) })
      .then(({ data }) => setData(data));
  }, [sku, applied]);

  // Memoização: séries/opções dos charts estáveis entre renders
  const serie = useMemo(() => {
    if (!data) return [];
    if (gran === "day") return data.series;
    const agg = {};
    for (const r of data.series) {
      const k = gran === "month" ? r.d.slice(0, 7) : isoWeek(r.d);
      agg[k] = agg[k] || { d: k, qty: 0, value: 0, orders: 0 };
      agg[k].qty += r.qty;
      agg[k].value += r.value;
      agg[k].orders += r.orders;
    }
    return Object.values(agg).sort((a, b) => (a.d > b.d ? 1 : -1));
  }, [data, gran]);
  const base = useMemo(() => apexBase(dark), [dark]);
  const lineSeries = useMemo(() => [
    { name: "Quantidade", data: serie.map((r) => r.qty) },
    { name: "Valor (R$)", data: serie.map((r) => Math.round(r.value)) },
  ], [serie]);
  const lineOpts = useMemo(() => ({
    ...base, chart: { ...base.chart, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: [3, 2] },
    xaxis: { categories: serie.map((r) => r.d), labels: { rotate: -45, style: { fontSize: "9px" } } },
    yaxis: [{ title: { text: "Qtd" } }, { opposite: true, title: { text: "R$" } }],
  }), [base, serie]);

  if (!data) return <div className="tile p-6 text-center text-zinc-400 animate-pulse">Carregando SKU...</div>;

  return (
    <div className="tile p-4 space-y-3" data-testid="pc-sku-detail">
      <div className="flex flex-wrap items-baseline gap-2 border-b border-zinc-200 dark:border-zinc-700 pb-2">
        <span className="font-mono font-black text-lg">{data.sku}</span>
        <span className="text-sm text-zinc-500 truncate max-w-md">{data.name}</span>
        {data.category && <span className="text-[10px] px-2 py-0.5 bg-zinc-100 dark:bg-zinc-800 border rounded">{data.category}</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-2">
        <Stat label="Peças produzidas" value={fmtInt(data.produced_pieces)} />
        <Stat label="Qtd vendida" value={fmtInt(data.sold_qty)} />
        <Stat label="Valor vendido" value={fmtBRL(data.sold_value)} />
        <Stat label="Pedidos" value={fmtInt(data.orders)} />
        <Stat label="Clientes" value={fmtInt(data.customers_count)} />
        <Stat label="Venda média" value={fmtBRL(data.avg_sale)} />
        <Stat label="Maior venda" value={fmtBRL(data.max_sale)} />
        <Stat label="Menor venda" value={fmtBRL(data.min_sale)} />
        <Stat label="1ª venda" value={data.first_sale || "—"} />
        <Stat label="Última venda" value={data.last_sale || "—"} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="overline text-[10px] text-zinc-500">EVOLUÇÃO DAS VENDAS</div>
          <div className="flex gap-1">
            {SKU_GRAN_OPTIONS.map(([k, l]) => (
              <button key={k} onClick={() => setGran(k)}
                className={`text-[10px] px-2 py-0.5 border rounded ${gran === k ? "bg-black text-white border-black" : "border-zinc-300"}`}
                data-testid={`pc-sku-gran-${k}`}>{l}</button>
            ))}
          </div>
        </div>
        {serie.length === 0 ? (
          <div className="text-xs text-zinc-400 py-6 text-center">Sem vendas no período</div>
        ) : (
          <Chart type="line" height={220} series={lineSeries} options={lineOpts} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniList title="TOP CLIENTES" rows={data.top_customers.map((c) => [c.customer, `${fmtInt(c.qty)} · ${fmtBRL(c.value)}`])} />
        <MiniList title="POR ESTADO" rows={data.by_state.map((s) => [s.state, fmtInt(s.qty)])} />
        <MiniList title="COLABORADORES ENVOLVIDOS" rows={data.collaborators.map((c) => [c.name, `${fmtInt(c.movements)} mov`])} />
      </div>
    </div>
  );
}

function isoWeek(dstr) {
  const d = new Date(dstr + "T12:00:00");
  const jan = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - jan) / 86400000 + jan.getDay() + 1) / 7);
  return `${d.getFullYear()}-S${String(week).padStart(2, "0")}`;
}

function MiniList({ title, rows }) {
  return (
    <div>
      <div className="overline text-[10px] text-zinc-500 mb-1">{title}</div>
      <div className="space-y-0.5">
        {rows.length === 0 && <div className="text-[10px] text-zinc-400">—</div>}
        {rows.map(([a, b], i) => (
          <div key={`${a}-${i}`} className="flex justify-between text-xs border-b border-zinc-100 dark:border-zinc-800 pb-0.5">
            <span className="truncate max-w-[65%]">{a}</span>
            <span className="font-mono text-zinc-500">{b}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SkusTab({ applied }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("sold_qty");
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState(null);
  const limit = 25;

  useEffect(() => { setSkip(0); setSelected(null); }, [applied]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/production-reports/by-sku", { params: { ...buildParams(applied), search, sort, skip, limit } })
        .then(({ data }) => setData(data));
    }, 300);
    return () => clearTimeout(t);
  }, [applied, search, sort, skip]);

  return (
    <div className="space-y-3" data-testid="pc-skus-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input className="input !pl-7 text-xs w-56" placeholder="Buscar SKU ou nome..."
            value={search} onChange={(e) => { setSearch(e.target.value); setSkip(0); }} data-testid="pc-sku-search" />
        </div>
        <select className="input text-xs w-auto" value={sort} onChange={(e) => { setSort(e.target.value); setSkip(0); }} data-testid="pc-sku-sort">
          <option value="sold_qty">Mais vendidos</option>
          <option value="produced_pieces">Mais produzidos</option>
          <option value="value">Maior valor</option>
          <option value="sold_orders">Mais pedidos</option>
          <option value="sku">SKU (A-Z)</option>
        </select>
        {data && <span className="text-[10px] text-zinc-500 ml-auto">{fmtInt(data.total)} SKUs</span>}
      </div>

      {!data ? (
        <div className="tile p-8 text-center text-zinc-400 animate-pulse">Carregando SKUs...</div>
      ) : (
        <div className="tile overflow-x-auto">
          <table className="table w-full text-xs">
            <thead>
              <tr>
                <th>SKU</th><th>Nome</th><th>Tipo</th><th className="text-right">Peças produzidas</th>
                <th className="text-right">Qtd vendida</th><th className="text-right">Pedidos</th>
                <th className="text-right">Valor</th><th className="text-right">Preço médio</th>
                <th className="text-right">Clientes</th><th className="text-right">Estoque</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.sku}
                  className={`cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 ${selected === r.sku ? "bg-yellow-50 dark:bg-zinc-800" : ""}`}
                  onClick={() => setSelected(selected === r.sku ? null : r.sku)}
                  data-testid={`pc-sku-row-${r.sku}`}>
                  <td className="font-mono font-bold">{r.sku}</td>
                  <td className="truncate max-w-[260px]">{r.name}</td>
                  <td className="text-zinc-500">{r.type || "—"}</td>
                  <td className="text-right font-mono font-bold">{fmtInt(r.produced_pieces)}</td>
                  <td className="text-right font-mono">{fmtInt(r.sold_qty)}</td>
                  <td className="text-right font-mono">{fmtInt(r.sold_orders)}</td>
                  <td className="text-right font-mono">{fmtBRL(r.value)}</td>
                  <td className="text-right font-mono">{fmtBRL(r.avg_price)}</td>
                  <td className="text-right font-mono">{fmtInt(r.customers)}</td>
                  <td className="text-right font-mono">{r.stock ?? "—"}</td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={10} className="text-center text-zinc-400 py-6">Nenhum SKU no período</td></tr>
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-end gap-1 p-2">
            <button className="btn !py-0.5 !px-2" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>‹</button>
            <span className="text-[10px] text-zinc-500">{Math.floor(skip / limit) + 1} / {Math.max(1, Math.ceil(data.total / limit))}</span>
            <button className="btn !py-0.5 !px-2" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>›</button>
          </div>
        </div>
      )}

      {selected && <SkuDetail sku={selected} applied={applied} />}
    </div>
  );
}
